import React, { useState, useCallback, useMemo, useRef } from "react";
import { useHistory } from "react-router-dom";
import * as yup from "yup";
import { Text, Flex, toast, FileUpload, type FileObjectType } from "@jsq/ds/core";
import {
  Form,
  FormTextInput,
  FormTextarea,
  FormSelect,
  SubmitButton,
  useFormContext,
} from "@jsq/ds/forms";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { type DataStudioJobTypeEnum } from "~/graphql/supergraphTypes";
import useDataStudioUploadFiles from "~/hooks/useDataStudioUploadFiles/useDataStudioUploadFiles";
import { validateEmail } from "~/pages/DataStudio/utils/validation";
import { EMAIL_DOMAIN_REGEX, ARENA_DOMAIN_REGEX } from "~/utils/constants";
import { AdvancedPermissionsSection } from "./components/AdvancedPermissionsSection";
import * as styles from "./styles_vanilla.css";
import {
  buildToolConfiguration,
  validateRequiredFiles,
  mapUploadedFiles,
  checkUploadSuccess,
  hasFileValidationErrors,
} from "./utils";
import { validateProvisioningFlags } from "./utils/provisioningFlags";
import type {
  ArenaCreationProps,
  FormValues,
  RequiredInformationSectionProps,
  OptionalConfigurationSectionProps,
  AdvancedPermissionsSectionProps,
  AdvancedPermissionFileType,
} from "./utils/types";

const ARENA_CREATION_JOB_TYPE: DataStudioJobTypeEnum = "ARENA_CREATION";

const initialValues: FormValues = {
  arenaName: "",
  environment: "test",
  domain: "",
  emailDomain: "",
  jsqCustomerId: "",
  userEmailId: "",
  userFirstName: "",
  userLastName: "",
  edition: "full",
  timezone: "US/Pacific",
  provisioningFlags: "",
};

const yupValidationSchema: yup.ObjectSchema<FormValues> = yup.object({
  arenaName: yup.string().required("Arena name is required").trim(),
  environment: yup.string().required("Environment is required"),
  domain: yup
    .string()
    .required("Arena domain is required")
    .trim()
    .matches(
      ARENA_DOMAIN_REGEX,
      "Arena domain must contain only letters, numbers, hyphens, and dots. No underscores allowed",
    ),
  emailDomain: yup
    .string()
    .required("Email domain is required")
    .trim()
    .matches(
      EMAIL_DOMAIN_REGEX,
      "Please enter a valid email domain (e.g., domain.com, domain.in)",
    ),
  jsqCustomerId: yup.string().trim().default("").matches(/^\d+$/, {
    message:
      "JSQ Customer ID must be a non-negative integer (use 0 for internal Juniper Square arenas)",
    excludeEmptyString: true,
  }),
  userEmailId: yup
    .string()
    .required("User email is required")
    .test("valid-email", "Please enter a valid email address format", (value) => {
      if (!value) return true;
      const errorMessage = validateEmail(value);
      return errorMessage === "";
    }),
  userFirstName: yup.string().required("First name is required").trim(),
  userLastName: yup.string().required("Last name is required").trim(),
  edition: yup.string().required(),
  timezone: yup.string().required("Timezone is required"),
  provisioningFlags: yup
    .string()
    .trim()
    .default("")
    .test("valid-flags", "Invalid provisioning flags format", (value) => {
      if (!value) return true; // Empty is valid (optional field)
      const validation = validateProvisioningFlags(value);
      return validation.isValid;
    }),
});

const RequiredInformationSection: React.FC<RequiredInformationSectionProps> = ({
  isSubmitting,
}) => (
  <div className={styles.section}>
    <Flex alignItems="center" gap={8} className={styles.sectionHeader}>
      <Icon type="exclamation-triangle" size="medium" color={Colors.Danger500} />
      <Text variant="strong-lg" color={Colors.Neutral900}>
        Required Information
      </Text>
    </Flex>

    <div className={styles.formGrid}>
      <FormTextInput
        name="arenaName"
        label="Arena Name"
        placeholder="Enter arena name"
        required
        disabled={isSubmitting}
      />
      <FormSelect
        name="environment"
        label="Environment"
        placeholder="Select environment"
        full
        disabled={isSubmitting}
      >
        <FormSelect.Section>
          <FormSelect.Option value="test">Test</FormSelect.Option>
          <FormSelect.Option value="production">Production</FormSelect.Option>
        </FormSelect.Section>
      </FormSelect>
      <FormTextInput
        name="domain"
        label="Arena Domain"
        placeholder="myarena.exar"
        required
        disabled={isSubmitting}
      />
      <FormTextInput
        name="emailDomain"
        label="Email Domain"
        placeholder="myarena.com"
        required
        disabled={isSubmitting}
      />
      <FormTextInput
        name="userEmailId"
        label="User Email"
        placeholder="user@example.com"
        required
        disabled={isSubmitting}
      />
      <FormTextInput
        name="userFirstName"
        label="First Name"
        placeholder="John"
        required
        disabled={isSubmitting}
      />
      <FormTextInput
        name="userLastName"
        label="Last Name"
        placeholder="Doe"
        required
        disabled={isSubmitting}
      />
    </div>
  </div>
);

const OptionalConfigurationSection: React.FC<OptionalConfigurationSectionProps> = ({
  isSubmitting,
}) => (
  <div className={styles.section}>
    <Flex alignItems="center" gap={8} className={styles.sectionHeader}>
      <Icon type="cog" size="medium" color={Colors.Primary500} />
      <Text variant="strong-lg" color={Colors.Neutral900}>
        Optional Configuration
      </Text>
    </Flex>

    <div className={styles.formGrid}>
      <div>
        <FormSelect
          name="edition"
          label="Edition"
          placeholder="Select edition"
          full
          disabled={isSubmitting}
        >
          <FormSelect.Section>
            <FormSelect.Option value="full">Full</FormSelect.Option>
            <FormSelect.Option value="gp_lite_self_serve">GP Lite Self Serve</FormSelect.Option>
            <FormSelect.Option value="gp_lite_assisted">GP Lite Assisted</FormSelect.Option>
            <FormSelect.Option value="sponsor">Sponsor</FormSelect.Option>
            <FormSelect.Option value="professional">Professional</FormSelect.Option>
            <FormSelect.Option value="institutional">Institutional</FormSelect.Option>
          </FormSelect.Section>
        </FormSelect>
        <Text
          variant="regular-sm"
          color={Colors.Neutral600}
          className={styles.fieldDescription}
        >
          Choose the arena edition that best fits your needs (defaults to &quot;full&quot;)
        </Text>
      </div>

      <div>
        <FormSelect
          name="timezone"
          label="Timezone"
          placeholder="Select timezone"
          full
          disabled={isSubmitting}
        >
          <FormSelect.Section>
            <FormSelect.Option value="US/Alaska">US/Alaska</FormSelect.Option>
            <FormSelect.Option value="US/Hawaii">US/Hawaii</FormSelect.Option>
            <FormSelect.Option value="US/Arizona">US/Arizona</FormSelect.Option>
            <FormSelect.Option value="US/Pacific">US/Pacific</FormSelect.Option>
            <FormSelect.Option value="US/Mountain">US/Mountain</FormSelect.Option>
            <FormSelect.Option value="US/Central">US/Central</FormSelect.Option>
            <FormSelect.Option value="US/Eastern">US/Eastern</FormSelect.Option>
            <FormSelect.Option value="UTC">UTC</FormSelect.Option>
            <FormSelect.Option value="Europe/London">Europe/London</FormSelect.Option>
            <FormSelect.Option value="Europe/Paris">Europe/Paris</FormSelect.Option>
            <FormSelect.Option value="Asia/Tokyo">Asia/Tokyo</FormSelect.Option>
            <FormSelect.Option value="Asia/Shanghai">Asia/Shanghai</FormSelect.Option>
            <FormSelect.Option value="Australia/Sydney">Australia/Sydney</FormSelect.Option>
          </FormSelect.Section>
        </FormSelect>
        <Text
          variant="regular-sm"
          color={Colors.Neutral600}
          className={styles.fieldDescription}
        >
          Select the timezone for your arena (defaults to &quot;US/Pacific&quot;)
        </Text>
      </div>

      <div>
        <FormTextInput
          name="jsqCustomerId"
          label="JSQ Customer ID"
          placeholder="0 for internal Juniper Square arenas"
          disabled={isSubmitting}
        />
        <Text
          variant="regular-sm"
          color={Colors.Neutral600}
          className={styles.fieldDescription}
        >
          Optional. Use 0 for internal Juniper Square arenas
        </Text>
      </div>
    </div>

    <div>
      <FormTextarea
        name="provisioningFlags"
        label="Provisioning Flags"
        placeholder="Enter comma-separated flags (e.g., flag1, flag2, flag3)"
        disabled={isSubmitting}
        isFullWidth={true}
      />
      <Text variant="regular-sm" color={Colors.Neutral600} className={styles.fieldDescription}>
        Optional comma-separated list of provisioning flags to be used during arena creation
      </Text>
    </div>
  </div>
);

interface ResetButtonProps {
  isSubmitting: boolean;
  onResetFiles: () => void;
}

const ResetButton: React.FC<ResetButtonProps> = ({ isSubmitting, onResetFiles }) => {
  const { resetForm } = useFormContext<FormValues>();

  const handleReset = useCallback(() => {
    resetForm();
    onResetFiles();
  }, [resetForm, onResetFiles]);

  return (
    <button
      type="button"
      onClick={handleReset}
      disabled={isSubmitting}
      className={styles.resetButton}
    >
      Reset Form
    </button>
  );
};

const ArenaCreation: React.FC<ArenaCreationProps> = () => {
  const [extensionFieldsFiles, setExtensionFieldsFiles] = useState<FileObjectType[]>([]);
  const [extensionEnumsFiles, setExtensionEnumsFiles] = useState<FileObjectType[]>([]);
  const [userRoleFiles, setUserRoleFiles] = useState<FileObjectType[]>([]);
  const [userProvisioningFiles, setUserProvisioningFiles] = useState<FileObjectType[]>([]);
  const [unitsFiles, setUnitsFiles] = useState<FileObjectType[]>([]);
  const [distributionListFiles, setDistributionListFiles] = useState<FileObjectType[]>([]);
  const [documentCategoryFiles, setDocumentCategoryFiles] = useState<FileObjectType[]>([]);
  const [capitalTransactionTypesFiles, setCapitalTransactionTypesFiles] = useState<
    FileObjectType[]
  >([]);
  const [distributionTypesFiles, setDistributionTypesFiles] = useState<FileObjectType[]>([]);
  const [netIncomeBasesFiles, setNetIncomeBasesFiles] = useState<FileObjectType[]>([]);
  const [netIncomeTypesFiles, setNetIncomeTypesFiles] = useState<FileObjectType[]>([]);
  const [navComponentsFiles, setNavComponentsFiles] = useState<FileObjectType[]>([]);
  const [entityTypesFiles, setEntityTypesFiles] = useState<FileObjectType[]>([]);

  // Use refs to track the latest file state immediately (to avoid React state timing issues)
  const capitalTransactionTypesFilesRef = useRef<FileObjectType[]>([]);
  const distributionTypesFilesRef = useRef<FileObjectType[]>([]);
  const netIncomeBasesFilesRef = useRef<FileObjectType[]>([]);
  const netIncomeTypesFilesRef = useRef<FileObjectType[]>([]);
  const navComponentsFilesRef = useRef<FileObjectType[]>([]);
  const entityTypesFilesRef = useRef<FileObjectType[]>([]);
  const [uploadProgress, setUploadProgress] = useState<string>("");
  const [uploadError, setUploadError] = useState<string>("");
  const [fileUploadKey, setFileUploadKey] = useState<number>(0);

  const {
    uploadFilesToS3: dataStudioUploadFiles,
    createJob: dataStudioCreateJob,
    loading: dataStudioLoading,
  } = useDataStudioUploadFiles();

  const history = useHistory();
  const isSubmitting = dataStudioLoading;

  // Check if any uploaded files have validation errors
  const hasFileErrors = useMemo(
    () =>
      hasFileValidationErrors(
        extensionFieldsFiles,
        extensionEnumsFiles,
        userRoleFiles,
        userProvisioningFiles,
        unitsFiles,
        distributionListFiles,
        documentCategoryFiles,
        capitalTransactionTypesFiles,
        distributionTypesFiles,
        netIncomeBasesFiles,
        netIncomeTypesFiles,
        navComponentsFiles,
        entityTypesFiles,
      ),
    [
      extensionFieldsFiles,
      extensionEnumsFiles,
      userRoleFiles,
      userProvisioningFiles,
      unitsFiles,
      distributionListFiles,
      documentCategoryFiles,
      capitalTransactionTypesFiles,
      distributionTypesFiles,
      netIncomeBasesFiles,
      netIncomeTypesFiles,
      navComponentsFiles,
      entityTypesFiles,
    ],
  );

  const handleExtensionFieldsFilesChange = useCallback((files: FileObjectType[]) => {
    setExtensionFieldsFiles(files);
  }, []);

  const handleExtensionEnumsFilesChange = useCallback((files: FileObjectType[]) => {
    setExtensionEnumsFiles(files);
  }, []);

  const handleUserRoleFilesChange = useCallback((files: FileObjectType[]) => {
    setUserRoleFiles(files);
  }, []);

  const handleUserProvisioningFilesChange = useCallback((files: FileObjectType[]) => {
    setUserProvisioningFiles(files);
  }, []);

  const handleUnitsFilesChange = useCallback((files: FileObjectType[]) => {
    setUnitsFiles(files);
  }, []);

  const handleDistributionListFilesChange = useCallback((files: FileObjectType[]) => {
    setDistributionListFiles(files);
  }, []);

  const handleDocumentCategoryFilesChange = useCallback((files: FileObjectType[]) => {
    setDocumentCategoryFiles(files);
  }, []);

  const handleAdvancedPermissionFileChange = useCallback(
    (type: AdvancedPermissionFileType, files: FileObjectType[]) => {
      switch (type) {
        case "capitalTransactionTypes":
          capitalTransactionTypesFilesRef.current = files;
          setCapitalTransactionTypesFiles(files);
          break;
        case "distributionTypes":
          distributionTypesFilesRef.current = files;
          setDistributionTypesFiles(files);
          break;
        case "netIncomeBases":
          netIncomeBasesFilesRef.current = files;
          setNetIncomeBasesFiles(files);
          break;
        case "netIncomeTypes":
          netIncomeTypesFilesRef.current = files;
          setNetIncomeTypesFiles(files);
          break;
        case "navComponents":
          navComponentsFilesRef.current = files;
          setNavComponentsFiles(files);
          break;
        case "entityTypes":
          entityTypesFilesRef.current = files;
          setEntityTypesFiles(files);
          break;
      }
    },
    [],
  );

  const handleResetFiles = useCallback(() => {
    setExtensionFieldsFiles([]);
    setExtensionEnumsFiles([]);
    setUserRoleFiles([]);
    setUserProvisioningFiles([]);
    setUnitsFiles([]);
    setDistributionListFiles([]);
    setDocumentCategoryFiles([]);
    setCapitalTransactionTypesFiles([]);
    setDistributionTypesFiles([]);
    setNetIncomeBasesFiles([]);
    setNetIncomeTypesFiles([]);
    setNavComponentsFiles([]);
    setEntityTypesFiles([]);
    // Also clear refs
    capitalTransactionTypesFilesRef.current = [];
    distributionTypesFilesRef.current = [];
    netIncomeBasesFilesRef.current = [];
    netIncomeTypesFilesRef.current = [];
    navComponentsFilesRef.current = [];
    entityTypesFilesRef.current = [];
    setUploadProgress("");
    setUploadError("");
    setFileUploadKey((prev) => prev + 1);
  }, []);

  const handleSubmit = useCallback(
    async (values: Partial<FormValues>) => {
      setUploadProgress("");
      setUploadError("");

      try {
        // Validate required files
        const validation = validateRequiredFiles(userRoleFiles, userProvisioningFiles);
        if (!validation.isValid) {
          toast.error(validation.errorMessage);
          return;
        }

        const arenaName = (values.arenaName || "").trim();
        const domain = (values.domain || "").trim().toLowerCase();
        const emailDomain = (values.emailDomain || "").trim();
        const jsqCustomerId = (values.jsqCustomerId || "").trim();
        const userEmailId = (values.userEmailId || "").trim().toLowerCase();
        const userFirstName = (values.userFirstName || "").trim();
        const userLastName = (values.userLastName || "").trim();

        // Collect all files that need to be uploaded - use refs for advanced permissions files
        const allFiles = [
          ...extensionFieldsFiles,
          ...extensionEnumsFiles,
          ...userRoleFiles,
          ...userProvisioningFiles,
          ...unitsFiles,
          ...distributionListFiles,
          ...documentCategoryFiles,
          ...capitalTransactionTypesFilesRef.current,
          ...distributionTypesFilesRef.current,
          ...netIncomeBasesFilesRef.current,
          ...netIncomeTypesFilesRef.current,
          ...navComponentsFilesRef.current,
          ...entityTypesFilesRef.current,
        ];

        setUploadProgress("Uploading files...");

        // Prepare files for DataStudio upload
        const filesToUpload = allFiles.filter((file) => file.status === "accepted");

        // Upload all files using DataStudio presigned URL mutation
        const uploadResponse = await dataStudioUploadFiles({
          environment: values.environment || "test",
          jobType: ARENA_CREATION_JOB_TYPE,
          arenaDomain: domain,
          arenaUserEmail: userEmailId,
          files: filesToUpload,
        });

        // Check if all files uploaded successfully
        checkUploadSuccess(uploadResponse);

        setUploadProgress("Creating arena...");

        // Map uploaded files by type - use refs for advanced permissions files
        const mappedFiles = mapUploadedFiles(
          uploadResponse,
          extensionFieldsFiles,
          extensionEnumsFiles,
          userRoleFiles,
          userProvisioningFiles,
          unitsFiles,
          distributionListFiles,
          documentCategoryFiles,
          capitalTransactionTypesFilesRef.current,
          distributionTypesFilesRef.current,
          netIncomeBasesFilesRef.current,
          netIncomeTypesFilesRef.current,
          navComponentsFilesRef.current,
          entityTypesFilesRef.current,
        );

        // Build tools array for job configuration
        const tools = buildToolConfiguration({
          arenaName,
          domain,
          emailDomain,
          jsqCustomerId,
          edition: values.edition || "full",
          timezone: values.timezone || "US/Pacific",
          userEmailId,
          userFirstName,
          userLastName,
          provisioningFlags: values.provisioningFlags
            ? values.provisioningFlags.trim() || undefined
            : undefined,
          extensionFieldsFile: mappedFiles.extensionFieldsFile,
          extensionEnumsFile: mappedFiles.extensionEnumsFile,
          userRolesFile: mappedFiles.userRolesFile,
          userProvisioningFile: mappedFiles.userProvisioningFile,
          unitsFile: mappedFiles.unitsFile,
          distributionListFile: mappedFiles.distributionListFile,
          documentCategoryFile: mappedFiles.documentCategoryFile,
          capitalTransactionTypesFile: mappedFiles.capitalTransactionTypesFile,
          distributionTypesFile: mappedFiles.distributionTypesFile,
          netIncomeBasesFile: mappedFiles.netIncomeBasesFile,
          netIncomeTypesFile: mappedFiles.netIncomeTypesFile,
          navComponentsFile: mappedFiles.navComponentsFile,
          entityTypesFile: mappedFiles.entityTypesFile,
        });

        // Create arena job using DataStudio mutation
        const jobResponse = await dataStudioCreateJob({
          jobType: ARENA_CREATION_JOB_TYPE,
          arenaDomain: domain,
          arenaName,
          arenaUserEmail: userEmailId,
          env: values.environment || "test",
          jobConfig: {
            tools,
          },
        });

        if (jobResponse.success) {
          toast.success("Arena creation job started successfully!");
          setUploadProgress("Arena creation job started successfully!");

          // Reset form
          setExtensionFieldsFiles([]);
          setExtensionEnumsFiles([]);
          setUserRoleFiles([]);
          setUserProvisioningFiles([]);
          setUnitsFiles([]);
          setDistributionListFiles([]);
          setDocumentCategoryFiles([]);

          // Redirect to dashboard after successful creation
          history.push("/data-studio/dashboard");
        } else {
          toast.error("Failed to create arena job");
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
        setUploadProgress("");
        setUploadError(errorMessage);
        toast.error(`Failed to create arena: ${errorMessage}`);
      }
    },
    [
      extensionFieldsFiles,
      extensionEnumsFiles,
      userRoleFiles,
      userProvisioningFiles,
      unitsFiles,
      distributionListFiles,
      documentCategoryFiles,
      dataStudioUploadFiles,
      dataStudioCreateJob,
      history,
    ],
  );

  return (
    <Flex
      data-testid="data-studio-arena-creation"
      flexDirection="column"
      className={styles.container}
    >
      {/* Header */}
      <Flex alignItems="center" gap={16} className={styles.header}>
        <Icon type="plus" size="large" color={Colors.Neutral900} />
        <Flex flexDirection="column">
          <Text variant="strong-xl" color={Colors.Neutral900}>
            Create Arena
          </Text>
          <Text variant="regular-md" color={Colors.Neutral600}>
            Set up your arena environment with custom configurations and optional file uploads
          </Text>
        </Flex>
      </Flex>

      {/* Form */}
      <div className={styles.form}>
        <Form<FormValues>
          initialValues={initialValues}
          yupValidationSchema={yupValidationSchema}
          onSubmit={handleSubmit}
        >
          <RequiredInformationSection isSubmitting={isSubmitting} />
          <OptionalConfigurationSection isSubmitting={isSubmitting} />

          {/* Extension Files (Optional) */}
          <div className={styles.section}>
            <Flex alignItems="center" gap={8} className={styles.sectionHeader}>
              <Icon type="upload" size="medium" color={Colors.Primary500} />
              <Text variant="strong-lg" color={Colors.Neutral900}>
                Extension Files (Optional)
              </Text>
            </Flex>

            <div className={styles.formGrid}>
              <div>
                <Text
                  variant="strong-md"
                  color={Colors.Neutral900}
                  className={styles.fileLabel}
                >
                  Extension Fields File
                </Text>
                <FileUpload
                  key={`extension-fields-${fileUploadKey}`}
                  uploadMessage="Drop the file here or"
                  acceptedFileTypes={[".csv"]}
                  onFileSelected={handleExtensionFieldsFilesChange}
                  initialFiles={extensionFieldsFiles}
                  isFullWidth
                  canAllowMultiple={false}
                  maxAcceptedFilesToDisplay={1}
                  isDisabled={isSubmitting}
                />
                <Text
                  variant="regular-sm"
                  color={Colors.Neutral600}
                  className={styles.fieldDescription}
                >
                  Upload a CSV file containing custom field definitions for your arena. Files
                  with &quot;field&quot; in the filename will be automatically mapped to
                  extension_fields_file parameter. Only .CSV format is supported.
                </Text>
              </div>

              <div>
                <Text
                  variant="strong-md"
                  color={Colors.Neutral900}
                  className={styles.fileLabel}
                >
                  Extension Enums File
                </Text>
                <FileUpload
                  key={`extension-enums-${fileUploadKey}`}
                  uploadMessage="Drop the file here or"
                  acceptedFileTypes={[".csv"]}
                  onFileSelected={handleExtensionEnumsFilesChange}
                  initialFiles={extensionEnumsFiles}
                  isFullWidth
                  canAllowMultiple={false}
                  maxAcceptedFilesToDisplay={1}
                  isDisabled={isSubmitting}
                />
                <Text
                  variant="regular-sm"
                  color={Colors.Neutral600}
                  className={styles.fieldDescription}
                >
                  Upload a CSV file containing custom enumeration values for your arena. Files
                  with &quot;enum&quot; in the filename will be automatically mapped to
                  extension_enums_file parameter. Only .CSV format is supported.
                </Text>
              </div>
            </div>
          </div>

          {/* User Role Provisioning */}
          <div className={styles.section}>
            <Flex alignItems="center" gap={8} className={styles.sectionHeader}>
              <Icon type="users" size="medium" color={Colors.Warning500} />
              <Text variant="strong-lg" color={Colors.Neutral900}>
                User Role Provisioning *
              </Text>
            </Flex>

            <div>
              <Text variant="strong-md" color={Colors.Neutral900} className={styles.fileLabel}>
                User Roles CSV File *
              </Text>
              <FileUpload
                key={`user-roles-${fileUploadKey}`}
                uploadMessage="Drop the file here or"
                acceptedFileTypes={[".csv"]}
                onFileSelected={handleUserRoleFilesChange}
                initialFiles={userRoleFiles}
                isFullWidth
                canAllowMultiple={false}
                maxAcceptedFilesToDisplay={1}
                isDisabled={isSubmitting}
              />
              <Text
                variant="regular-sm"
                color={Colors.Neutral600}
                className={styles.fieldDescription}
              >
                Upload a CSV file containing user role assignments for your arena. Only .CSV
                format is supported.
              </Text>
            </div>
          </div>

          {/* User Provisioning */}
          <div className={styles.section}>
            <Flex alignItems="center" gap={8} className={styles.sectionHeader}>
              <Icon type="user-plus" size="medium" color={Colors.Success500} />
              <Text variant="strong-lg" color={Colors.Neutral900}>
                User Provisioning *
              </Text>
            </Flex>

            <div>
              <Text variant="strong-md" color={Colors.Neutral900} className={styles.fileLabel}>
                User Provisioning TSV File *
              </Text>
              <FileUpload
                key={`user-provisioning-${fileUploadKey}`}
                uploadMessage="Drop the file here or"
                acceptedFileTypes={[".tsv"]}
                onFileSelected={handleUserProvisioningFilesChange}
                initialFiles={userProvisioningFiles}
                isFullWidth
                canAllowMultiple={false}
                maxAcceptedFilesToDisplay={1}
                isDisabled={isSubmitting}
              />
              <Text
                variant="regular-sm"
                color={Colors.Neutral600}
                className={styles.fieldDescription}
              >
                Upload a TSV file for user provisioning. This will be used with the
                --user_role_mapping_file parameter. Only .TSV format is supported.
              </Text>
            </div>
          </div>

          {/* Advanced Permissions Section */}
          <AdvancedPermissionsSection
            isSubmitting={isSubmitting}
            unitsFiles={unitsFiles}
            distributionListFiles={distributionListFiles}
            documentCategoryFiles={documentCategoryFiles}
            capitalTransactionTypesFiles={capitalTransactionTypesFiles}
            distributionTypesFiles={distributionTypesFiles}
            netIncomeBasesFiles={netIncomeBasesFiles}
            netIncomeTypesFiles={netIncomeTypesFiles}
            navComponentsFiles={navComponentsFiles}
            entityTypesFiles={entityTypesFiles}
            onUnitsFilesChange={handleUnitsFilesChange}
            onDistributionListFilesChange={handleDistributionListFilesChange}
            onDocumentCategoryFilesChange={handleDocumentCategoryFilesChange}
            onCapitalTransactionTypesFilesChange={(files) =>
              handleAdvancedPermissionFileChange("capitalTransactionTypes", files)
            }
            onDistributionTypesFilesChange={(files) =>
              handleAdvancedPermissionFileChange("distributionTypes", files)
            }
            onNetIncomeBasesFilesChange={(files) =>
              handleAdvancedPermissionFileChange("netIncomeBases", files)
            }
            onNetIncomeTypesFilesChange={(files) =>
              handleAdvancedPermissionFileChange("netIncomeTypes", files)
            }
            onNavComponentsFilesChange={(files) =>
              handleAdvancedPermissionFileChange("navComponents", files)
            }
            onEntityTypesFilesChange={(files) =>
              handleAdvancedPermissionFileChange("entityTypes", files)
            }
            fileUploadKey={fileUploadKey}
          />

          {/* Submit Button */}
          <div className={styles.submitSection}>
            <div className={styles.statusMessages}>
              {uploadProgress && (
                <Text
                  variant="regular-sm"
                  color={Colors.Neutral600}
                  className={styles.uploadProgress}
                >
                  {uploadProgress}
                </Text>
              )}
              {uploadError && (
                <Text
                  variant="regular-sm"
                  color={Colors.Danger500}
                  className={styles.uploadError}
                >
                  {uploadError}
                </Text>
              )}
            </div>
            <div className={styles.buttonGroup}>
              <ResetButton isSubmitting={isSubmitting} onResetFiles={handleResetFiles} />
              <SubmitButton
                isLoading={isSubmitting}
                disabled={hasFileErrors}
                className={styles.submitButton}
              >
                {isSubmitting ? "Creating Arena..." : "Create Arena"}
              </SubmitButton>
            </div>
          </div>
        </Form>
      </div>
    </Flex>
  );
};

export { ArenaCreation };
export type {
  ArenaCreationProps,
  FormValues,
  RequiredInformationSectionProps,
  OptionalConfigurationSectionProps,
  AdvancedPermissionsSectionProps,
};
